import React from 'react';
import {styled} from '@mui/material/styles';
import {Box, Button, TableBody, TableCell, TableHead, TableRow, Typography, Table} from '@mui/material';
import {tableCellClasses} from '@mui/material/TableCell';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ExpandableText from 'src/components/ExpandableText';

const ANALYSIS_TEXT_CLAMP_MIN_CHARS = 60;

const TableContainer = styled(Box)(({theme}) => ({
    padding: '0',
    borderRadius: '8px',
    border: '1px solid',
    borderColor: theme.palette['surface-container-high'],
    marginTop: '16px',
    overflow: 'hidden'
}));

const NoDataBox = styled(Box)({
    display: 'flex',
    flexDirection: 'column'
});

const StyledTableCell = styled(TableCell)(({theme}) => ({
    padding: '10px 16px',
    border: 'none',
    fontVariantNumeric: 'lining-nums tabular-nums slashed-zero',
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette['surface-container-high'],
        color: theme.palette['on-surface'],
        textTransform: 'uppercase',
        fontSize: theme.styles.label['small - demi']?.fontSize,
        fontWeight: theme.styles.label['small - demi']?.fontWeight,
        lineHeight: theme.styles.label['small - demi']?.lineHeight,
        letterSpacing: theme.styles.label['small - demi']?.letterSpacing
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: theme.styles.body.medium.fontSize,
        fontWeight: theme.styles.body.medium.fontWeight,
        lineHeight: theme.styles.body.medium.lineHeight,
        letterSpacing: theme.styles.body.medium.letterSpacing,
        color: theme.palette.primary.main
    }
}));

interface RecentAnalysisTableProps {
    recentAnalyses: any[];
    formatTime: any;
}

interface AnalysisTableRowProps {
    row: any;
    showDescription: boolean;
    formatTime: any;
}

const statusIcons = {
    processing: {
        icon: <TimerOutlinedIcon sx={{fontSize: '14px', marginRight: '6px', color: '#F7218C'}} color="secondary" />,
        color: 'on-surface'
    },
    canceled: {
        icon: <ErrorOutlineOutlinedIcon sx={{fontSize: '14px', marginRight: '6px'}} color="error" />,
        color: 'error'
    },
    failed: {
        icon: <ErrorOutlineOutlinedIcon sx={{fontSize: '14px', marginRight: '6px'}} color="error" />,
        color: 'error'
    },
    completed: {
        icon: <CheckCircleOutlinedIcon sx={{fontSize: '14px', marginRight: '6px'}} color="primary" />,
        color: 'primary'
    },
    ready: {
        icon: <CheckCircleOutlinedIcon sx={{fontSize: '14px', marginRight: '6px'}} color="primary" />,
        color: 'primary'
    }
};

/**
 * Gets corresponding status info
 */
const getStatus = (
    status: string
): {
    icon: React.JSX.Element;
    color: string;
} | null => {
    const statusEntry = Object.entries(statusIcons).find(([key]) => key === status);
    return statusEntry ? statusEntry[1] : null;
};

const AnalysisTableRow: React.FC<AnalysisTableRowProps> = ({row, showDescription, formatTime}) => {
    const titleText = row?.title || '';
    const descriptionText = row?.description || '';

    return (
        <TableRow>
            <StyledTableCell sx={{width: 230}}>
                <Typography sx={{textTransform: 'uppercase', fontSize: 14}}>{`AN-${row?.analysis_num}`}</Typography>
            </StyledTableCell>
            <StyledTableCell sx={{width: 450}}>
                <ExpandableText value={titleText} maxChars={ANALYSIS_TEXT_CLAMP_MIN_CHARS} typographySx={{fontSize: 13}} />
            </StyledTableCell>
            {showDescription && (
                <StyledTableCell sx={{width: 450}}>
                    <ExpandableText value={descriptionText} maxChars={ANALYSIS_TEXT_CLAMP_MIN_CHARS} typographySx={{fontSize: 13}} />
                </StyledTableCell>
            )}
            <StyledTableCell sx={{width: 213}}>{formatTime(row?.time_started)}</StyledTableCell>
            <StyledTableCell sx={{width: 213}}>{row.status === 'completed' && formatTime(row.time_completed)}</StyledTableCell>
            <TableCell
                sx={{
                    border: 'none',
                    alignItems: 'center'
                }}>
                <Box display="flex" alignItems="center">
                    {getStatus(row.status)?.icon}
                    <Typography variant="label" size="small - demi" color={getStatus(row.status)?.color}>
                        {row.status}
                    </Typography>
                </Box>
            </TableCell>
            <TableCell sx={{border: 'none'}}>
                {row.status === 'completed' && (
                    <Button
                        component="a"
                        href={`${window.location.origin}/visualizer/?analysis_id=${row.analysis_id}&exp=${row.experiment_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        sx={{
                            padding: '4px 8px',
                            borderRadius: '12px',
                            backgroundColor: '#000',
                            fontSize: '12px',
                            height: 'unset',
                            fontFamily: 'Maison Neue',
                            color: '#fff',
                            lineHeight: '16px',
                            letterSpacing: '0.5px',
                            textTransform: 'uppercase',
                            '&:hover': {backgroundColor: '#000d', color: '#fff'}
                        }}>
                        Visualize
                    </Button>
                )}
            </TableCell>
        </TableRow>
    );
};

const AnalysisTable: React.FC<RecentAnalysisTableProps> = ({recentAnalyses, formatTime}) => {
    const showDescription = recentAnalyses?.some(a => (a?.description || '').trim().length > 0);

    return (
        <TableContainer>
            {recentAnalyses?.length ? (
                <>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>Analysis ID</StyledTableCell>
                                <StyledTableCell sx={{width: '130px'}}>Analysis Name</StyledTableCell>
                                {showDescription && <StyledTableCell sx={{width: '300px'}}>Description</StyledTableCell>}
                                <StyledTableCell sx={{width: '300px'}}>Time Started</StyledTableCell>
                                <StyledTableCell sx={{width: '300px'}}>Time Completed</StyledTableCell>
                                <StyledTableCell>Status</StyledTableCell>
                                <StyledTableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {recentAnalyses.map((row: any) => (
                                <AnalysisTableRow
                                    key={row.analysis_id}
                                    row={row}
                                    showDescription={showDescription}
                                    formatTime={formatTime}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </>
            ) : (
                <NoDataBox sx={{borderColor: 'outline'}}>
                    <Typography variant="body" size="large" sx={{margin: '0 auto', padding: '3% 0'}}>
                        No Analyses to display.
                    </Typography>
                </NoDataBox>
            )}
        </TableContainer>
    );
};

export default AnalysisTable;
